import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CraneSpecificationManager = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [editingSpec, setEditingSpec] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const craneSpecs = [
    {
      id: 1,
      make: "Liebherr",
      model: "LTM 1200-5.1",
      year: 2023,
      capacity: "200 tons",
      boomLength: "72m",
      jibLength: "19m",
      engineMake: "Liebherr",
      horsepower: 367,
      transportWeight: "48,000 kg",
      counterweight: "50,000 kg",
      liftHeight: "101m",
      status: "Active",
      lastModified: "2025-11-05",
      modifiedBy: "admin@craneprice.com"
    },
    {
      id: 2,
      make: "Tadano",
      model: "ATF 130G-5",
      year: 2022,
      capacity: "130 tons",
      boomLength: "60m",
      jibLength: "19m",
      engineMake: "Mercedes-Benz",
      horsepower: 367,
      transportWeight: "48,000 kg",
      counterweight: "32,000 kg",
      liftHeight: "84m",
      status: "Active",
      lastModified: "2025-11-04",
      modifiedBy: "admin@craneprice.com"
    },
    {
      id: 3,
      make: "Grove",
      model: "GMK5250L",
      year: 2021,
      capacity: "250 tons",
      boomLength: "70m",
      jibLength: "21m",
      engineMake: "Mercedes-Benz",
      horsepower: 394,
      transportWeight: "60,000 kg",
      counterweight: "80,000 kg",
      liftHeight: "106m",
      status: "Active",
      lastModified: "2025-11-03",
      modifiedBy: "system@craneprice.com"
    }
  ];

  const makeOptions = [
    { value: '', label: 'All Makes' },
    { value: 'Liebherr', label: 'Liebherr' },
    { value: 'Tadano', label: 'Tadano' },
    { value: 'Grove', label: 'Grove' },
    { value: 'Terex', label: 'Terex' },
    { value: 'Link-Belt', label: 'Link-Belt' }
  ];

  const filteredSpecs = craneSpecs?.filter(spec => {
    const matchesSearch = spec?.make?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         spec?.model?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesMake = !selectedMake || spec?.make === selectedMake;
    return matchesSearch && matchesMake;
  });

  const handleEdit = (spec) => {
    setEditingSpec({ ...spec });
  };

  const handleSave = () => {
    console.log('Saving specification:', editingSpec);
    setEditingSpec(null);
  };

  const handleCancel = () => {
    setEditingSpec(null);
  };

  const handleDelete = (specId) => {
    console.log('Deleting specification:', specId);
  };

  const handleAddNew = () => {
    setShowAddForm(true);
  };

  const handleExport = () => {
    console.log('Exporting crane specifications');
  };

  const handleBulkImport = () => {
    console.log('Opening bulk import dialog');
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by make or model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={makeOptions}
              value={selectedMake}
              onChange={setSelectedMake}
              placeholder="Filter by make"
            />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            variant="default"
            onClick={handleAddNew}
            iconName="Plus"
            iconPosition="left"
            size="sm"
          >
            Add Specification
          </Button>
          <Button
            variant="outline"
            onClick={handleBulkImport}
            iconName="Upload"
            iconPosition="left"
            size="sm"
          >
            Bulk Import
          </Button>
          <Button
            variant="ghost"
            onClick={handleExport}
            iconName="Download"
            iconPosition="left"
            size="sm"
          >
            Export
          </Button>
        </div>
      </div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Database" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Total Specifications</p>
              <p className="text-2xl font-semibold text-text-primary">1,247</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Active Models</p>
              <p className="text-2xl font-semibold text-text-primary">1,198</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Pending Review</p>
              <p className="text-2xl font-semibold text-text-primary">49</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Last Updated</p>
              <p className="text-sm font-medium text-text-primary">2 hours ago</p>
            </div>
          </div>
        </div>
      </div>
      {/* Specifications Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Make/Model</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Year</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Capacity</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Boom Length</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Engine</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Status</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Last Modified</th>
                <th className="text-right p-4 text-sm font-medium text-text-primary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSpecs?.map((spec) => (
                <tr key={spec?.id} className="border-t border-border hover:bg-muted/50">
                  {editingSpec && editingSpec?.id === spec?.id ? (
                    <>
                      <td className="p-4">
                        <div className="space-y-2">
                          <Input
                            type="text"
                            value={editingSpec?.make}
                            onChange={(e) => setEditingSpec({...editingSpec, make: e?.target?.value})}
                            placeholder="Make"
                            className="text-sm"
                          />
                          <Input
                            type="text"
                            value={editingSpec?.model}
                            onChange={(e) => setEditingSpec({...editingSpec, model: e?.target?.value})}
                            placeholder="Model"
                            className="text-sm"
                          />
                        </div>
                      </td>
                      <td className="p-4">
                        <Input
                          type="number"
                          value={editingSpec?.year}
                          onChange={(e) => setEditingSpec({...editingSpec, year: parseInt(e?.target?.value)})}
                          className="text-sm w-20"
                        />
                      </td>
                      <td className="p-4">
                        <Input
                          type="text"
                          value={editingSpec?.capacity}
                          onChange={(e) => setEditingSpec({...editingSpec, capacity: e?.target?.value})}
                          className="text-sm w-24"
                        />
                      </td>
                      <td className="p-4">
                        <Input
                          type="text"
                          value={editingSpec?.boomLength}
                          onChange={(e) => setEditingSpec({...editingSpec, boomLength: e?.target?.value})}
                          className="text-sm w-20"
                        />
                      </td>
                      <td className="p-4">
                        <div className="space-y-2">
                          <Input
                            type="text"
                            value={editingSpec?.engineMake}
                            onChange={(e) => setEditingSpec({...editingSpec, engineMake: e?.target?.value})}
                            placeholder="Engine Make"
                            className="text-sm"
                          />
                          <Input
                            type="number"
                            value={editingSpec?.horsepower}
                            onChange={(e) => setEditingSpec({...editingSpec, horsepower: parseInt(e?.target?.value)})}
                            placeholder="HP"
                            className="text-sm w-20"
                          />
                        </div>
                      </td>
                      <td className="p-4">
                        <Select
                          options={[
                            { value: 'Active', label: 'Active' },
                            { value: 'Inactive', label: 'Inactive' },
                            { value: 'Pending', label: 'Pending' }
                          ]}
                          value={editingSpec?.status}
                          onChange={(value) => setEditingSpec({...editingSpec, status: value})}
                        />
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-text-secondary">Editing...</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleSave}
                            iconName="Check"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancel}
                            iconName="X"
                          />
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-text-primary">{spec?.make}</p>
                          <p className="text-sm text-text-secondary">{spec?.model}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-text-primary">{spec?.year}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-text-primary">{spec?.capacity}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-text-primary">{spec?.boomLength}</span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm text-text-primary">{spec?.engineMake}</p>
                          <p className="text-xs text-text-secondary">{spec?.horsepower} HP</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          spec?.status === 'Active' ? 'bg-success/10 text-success' :
                          spec?.status === 'Inactive'? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                        }`}>
                          {spec?.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm text-text-primary">{spec?.lastModified}</p>
                          <p className="text-xs text-text-secondary">{spec?.modifiedBy}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(spec)}
                            iconName="Edit"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(spec?.id)}
                            iconName="Trash2"
                            className="text-error hover:text-error"
                          />
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Showing {filteredSpecs?.length} of 1,247 specifications
        </p>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft">
            Previous
          </Button>
          <Button variant="outline" size="sm">1</Button>
          <Button variant="default" size="sm">2</Button>
          <Button variant="outline" size="sm">3</Button>
          <Button variant="outline" size="sm" iconName="ChevronRight">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CraneSpecificationManager;